import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  ApiError,
  changeMePassword,
  confirmEmailVerification,
  logout as apiLogout,
  patchMeProfile,
  requestMeEmailVerification,
  saveScope,
} from "@/lib/api";
import { useMe } from "@/features/auth/useMe";
import {
  applyExplicitLogoutBoundary,
  removeAllScopedFromCache,
  unscopedKey,
} from "@/lib/queryClient";
import { revokeAllPlayerPhotos } from "@/features/player-photos/usePlayerPhoto";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";
import type { MePayload, MeTeam, UserProfile } from "@/lib/types";
import { Button } from "@/components/ui/Button";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";
import {
  AtSign,
  CheckCircle2,
  Clock3,
  Globe2,
  KeyRound,
  Laptop,
  LockKeyhole,
  Mail,
  Phone,
  ShieldCheck,
  Star,
  UserRound,
  UsersRound,
} from "lucide-react";

/**
 * M9a B1–B8 — `/me` account self-service shell.
 *
 * Four tabs (Profile · Password · Email · Sessions). `?tab=` route
 * search-param drives the active tab; `.catch(undefined)` falls
 * back to `profile`.
 *
 * Auth gate (B7): unauthenticated users redirect to
 * `/login?next=/me` (preserves their landing intent). The redirect
 * fires from a `useEffect` so the SPA shell still mounts on a
 * direct nav.
 */

type AccountTab = "profile" | "password" | "email" | "sessions";
const VALID_TABS: AccountTab[] = ["profile", "password", "email", "sessions"];

const TAB_ICONS: Record<AccountTab, ReactNode> = {
  profile: <UserRound size={15} strokeWidth={2.2} aria-hidden="true" />,
  password: <KeyRound size={15} strokeWidth={2.2} aria-hidden="true" />,
  email: <Mail size={15} strokeWidth={2.2} aria-hidden="true" />,
  sessions: <Laptop size={15} strokeWidth={2.2} aria-hidden="true" />,
};

const COMMON_TIMEZONES = [
  "UTC",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Toronto",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Madrid",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Athens",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

const COMMON_LOCALES = [
  { value: "en-US", label: "English (US)" },
  { value: "en-GB", label: "English (UK)" },
  { value: "es-ES", label: "Español" },
  { value: "fr-FR", label: "Français" },
  { value: "de-DE", label: "Deutsch" },
  { value: "pt-BR", label: "Português (BR)" },
  { value: "ja-JP", label: "日本語" },
];

export function AccountPage() {
  const me = useMe();
  const navigate = useNavigate({ from: "/me" });
  const search = useSearch({ from: "/me" });
  const tab: AccountTab = VALID_TABS.includes(search.tab as AccountTab)
    ? (search.tab as AccountTab)
    : "profile";

  // B7 — auth gate: unauth → /login?next=/me. Ref-flag prevents
  // the effect re-firing once the URL changes (TanStack Router
  // keeps the parent briefly mounted on intra-effect navigate).
  const authRedirectFired = useRef(false);
  useEffect(() => {
    if (me.isLoading) return;
    if (me.data?.user) return;
    if (authRedirectFired.current) return;
    authRedirectFired.current = true;
    void navigate({
      to: "/login",
      search: { next: "/me" },
      replace: true,
    });
  }, [me.isLoading, me.data, navigate]);

  if (me.isLoading) {
    return (
      <main
        data-testid="me-loading"
        className="min-h-screen flex items-center justify-center text-ink-muted"
      >
        Loading…
      </main>
    );
  }
  if (!me.data?.user) {
    return null;
  }

  return (
    <main
      data-testid="me-page"
      className="min-h-screen bg-canvas"
    >
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 px-4 py-6 sm:px-6 lg:px-8">
        <AccountHeader me={me.data} />

        <nav
          data-testid="me-tabs"
          role="tablist"
          aria-label="Account settings"
          className="-mx-1 overflow-x-auto px-1"
        >
          <div className="inline-flex min-w-full gap-1 rounded border border-rule bg-surface p-1 shadow-sm sm:min-w-0">
            {VALID_TABS.map((t) => (
              <TabButton
                key={t}
                active={tab === t}
                icon={TAB_ICONS[t]}
                onClick={() =>
                  navigate({
                    search: (prev) => ({ ...prev, tab: t }),
                    replace: false,
                  })
                }
                testid={`me-tab-${t}`}
              >
                {labelForTab(t)}
              </TabButton>
            ))}
          </div>
        </nav>

        <div className="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)] lg:items-start">
          <AccountSummary me={me.data} />
          <section
            data-testid="me-content"
            data-active-tab={tab}
            className="min-w-0 rounded border border-rule bg-surface p-4 shadow-sm sm:p-5"
          >
            {tab === "profile" ? <ProfileTab me={me.data} /> : null}
            {tab === "password" ? <PasswordTab /> : null}
            {tab === "email" ? <EmailTab profile={me.data.profile} /> : null}
            {tab === "sessions" ? <SessionsTab /> : null}
          </section>
        </div>
      </div>
    </main>
  );
}

function labelForTab(tab: AccountTab): string {
  return tab.charAt(0).toUpperCase() + tab.slice(1);
}

function TabButton({
  active,
  icon,
  onClick,
  children,
  testid,
}: {
  active: boolean;
  icon: ReactNode;
  onClick: () => void;
  children: ReactNode;
  testid: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      data-active={active ? "true" : undefined}
      className="inline-flex h-10 min-w-max flex-1 items-center justify-center gap-2 rounded px-3 font-display text-xs font-semibold uppercase tracking-wider text-ink-muted transition-colors hover:bg-field hover:text-ink data-[active=true]:bg-accent data-[active=true]:text-accent-ink sm:flex-none"
    >
      {icon}
      {children}
    </button>
  );
}

function AccountHeader({ me }: { me: MePayload }) {
  const displayName = me.user.display_name || me.profile.display_name || me.user.username;
  return (
    <header className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
      <div className="min-w-0">
        <p className="font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
          Account
        </p>
        <h1 className="font-display text-2xl uppercase tracking-wider text-ink sm:text-3xl">
          {displayName}
        </h1>
        <p className="mt-1 max-w-3xl text-sm leading-snug text-ink-muted">
          Manage login, contact, verification, and session controls for
          @{me.user.username}.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2 lg:justify-end">
        <StatusPill tone={me.profile.email_verified_at ? "good" : "muted"}>
          {me.profile.email_verified_at ? "Email verified" : "Email unverified"}
        </StatusPill>
        <StatusPill tone={me.user.is_global_admin ? "accent" : "muted"}>
          {me.user.is_global_admin ? "Global admin" : roleLabel(me.active_scope?.effective_role ?? me.user.role)}
        </StatusPill>
      </div>
    </header>
  );
}

function AccountSummary({ me }: { me: MePayload }) {
  const displayName = me.user.display_name || me.profile.display_name || me.user.username;
  const initials = getInitials(displayName);
  const activeTeam = me.active_scope?.team?.name ?? "No active team";
  const contact = me.profile.email || me.profile.phone || "No contact on file";
  const visibleTeams = me.teams.slice(0, 4);
  const extraTeams = Math.max(0, me.teams.length - visibleTeams.length);
  return (
    <aside className="rounded border border-rule bg-surface p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-ink font-display text-base font-semibold text-ink-inverse">
          {initials}
        </span>
        <div className="min-w-0">
          <p className="truncate font-display text-base font-semibold text-ink">
            {displayName}
          </p>
          <p className="truncate font-mono text-xs text-ink-muted">
            @{me.user.username}
          </p>
        </div>
      </div>
      <div className="mt-4 grid gap-2">
        <SummaryRow
          icon={<ShieldCheck size={15} strokeWidth={2.2} />}
          label="Access"
          value={me.user.is_global_admin ? "Global admin" : roleLabel(me.active_scope?.effective_role ?? me.user.role)}
        />
        <SummaryRow
          icon={<Globe2 size={15} strokeWidth={2.2} />}
          label="Team"
          value={activeTeam}
        />
        <SummaryRow
          icon={me.profile.email ? <AtSign size={15} strokeWidth={2.2} /> : <Phone size={15} strokeWidth={2.2} />}
          label="Contact"
          value={contact}
        />
        <SummaryRow
          icon={<Clock3 size={15} strokeWidth={2.2} />}
          label="Timezone"
          value={me.profile.timezone || "Not set"}
        />
      </div>
      <div className="mt-4 rounded border border-rule bg-elevated p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
            Assigned teams
          </p>
          <span className="rounded-full border border-rule bg-surface px-2 py-0.5 font-mono text-[0.6rem] text-ink-muted">
            {me.teams.length}
          </span>
        </div>
        {visibleTeams.length > 0 ? (
          <div className="mt-2 grid gap-1.5">
            {visibleTeams.map((team) => (
              <div
                key={team.id}
                className="flex min-w-0 items-center justify-between gap-2 rounded border border-rule/70 bg-surface px-2 py-1.5"
              >
                <span className="min-w-0 truncate text-xs text-ink">{team.name}</span>
                <span className="shrink-0 font-mono text-[0.58rem] uppercase text-ink-muted">
                  {roleLabel(team.membership_role)}
                </span>
              </div>
            ))}
            {extraTeams > 0 ? (
              <p className="px-1 text-xs text-ink-muted">
                +{extraTeams} more in Profile
              </p>
            ) : null}
          </div>
        ) : (
          <p className="mt-2 text-xs text-ink-muted">No team assignments yet.</p>
        )}
      </div>
    </aside>
  );
}

function SummaryRow({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2 rounded border border-rule bg-elevated px-3 py-2">
      <span className="text-ink-muted" aria-hidden="true">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block font-display text-[0.58rem] uppercase tracking-wider text-ink-muted">
          {label}
        </span>
        <span className="block truncate text-sm text-ink">{value}</span>
      </span>
    </div>
  );
}

function StatusPill({
  tone,
  children,
}: {
  tone: "accent" | "good" | "muted";
  children: ReactNode;
}) {
  const toneClass =
    tone === "accent"
      ? "border-accent bg-accent text-accent-ink"
      : tone === "good"
        ? "border-good/40 bg-good/10 text-good"
        : "border-rule bg-elevated text-ink-muted";
  return (
    <span
      className={`inline-flex h-8 items-center rounded-full border px-3 font-display text-[0.65rem] font-semibold uppercase tracking-wider ${toneClass}`}
    >
      {children}
    </span>
  );
}

function SectionHeader({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: ReactNode;
}) {
  return (
    <header className="grid gap-3 border-b border-rule pb-4 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-start">
      <span className="hidden h-10 w-10 items-center justify-center rounded bg-elevated text-ink-muted sm:inline-flex">
        {icon}
      </span>
      <div className="min-w-0">
        <h2 className="font-display text-lg uppercase tracking-wider text-ink">
          {title}
        </h2>
        <p className="mt-1 max-w-2xl text-sm leading-snug text-ink-muted">
          {description}
        </p>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// B2 + B3 — Profile tab
// ---------------------------------------------------------------------------

function ProfileTab({ me }: { me: MePayload }) {
  const profile = me.profile;
  const toast = useToast();
  const queryClient = useQueryClient();
  const applyAuth = useAuthStore((s) => s.applyMePayload);
  const applyScope = useScopeStore((s) => s.applyMePayload);
  const browserTz = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  }, []);

  const seedFromProfile = (p: UserProfile) => ({
    first_name: p.first_name ?? "",
    last_name: p.last_name ?? "",
    email: p.email ?? "",
    phone: p.phone ?? "",
    timezone: p.timezone ?? browserTz,
    locale: p.locale ?? "en-US",
    preferred_contact_method:
      (p.preferred_contact_method as "email" | "phone" | "none") ?? "none",
  });

  const [draft, setDraft] = useState(() => seedFromProfile(profile));
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Track whether the form has unsaved edits. We only re-seed from
  // an upstream profile refetch when the draft is pristine, so a
  // background `/api/me` refresh (window-focus refetch, scope swap,
  // another mutation invalidating the cache) cannot wipe what the
  // user is typing.
  const [dirty, setDirty] = useState(false);
  // Snapshot the baseline used for dirty-comparison, refreshed each
  // time we reseed from the server.
  const baselineRef = useRef(seedFromProfile(profile));

  // Refetch reseed: only fire when the form has no unsaved edits.
  useEffect(() => {
    if (dirty) return;
    const seeded = seedFromProfile(profile);
    baselineRef.current = seeded;
    setDraft(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const updateField = <K extends keyof typeof draft>(
    key: K,
    value: (typeof draft)[K],
  ) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      // Recompute dirty against the last server snapshot, NOT the
      // initial mount value, so a save → reseed → no-op edit cycle
      // returns to pristine correctly.
      const base = baselineRef.current;
      const isDirty =
        next.first_name !== base.first_name ||
        next.last_name !== base.last_name ||
        next.email !== base.email ||
        next.phone !== base.phone ||
        next.timezone !== base.timezone ||
        next.locale !== base.locale ||
        next.preferred_contact_method !== base.preferred_contact_method;
      setDirty(isDirty);
      return next;
    });
  };

  // `null` clears the stored field on the backend; `undefined` would
  // be dropped by `model_dump(exclude_unset=True)` and leave the
  // server value untouched. Always send the typed value (trimmed),
  // mapping empty → null so blanking out a field actually persists.
  const toBody = () => {
    const norm = (s: string): string | null => {
      const t = s.trim();
      return t.length === 0 ? null : t;
    };
    return {
      first_name: norm(draft.first_name),
      last_name: norm(draft.last_name),
      email: norm(draft.email),
      phone: norm(draft.phone),
      timezone: norm(draft.timezone),
      locale: norm(draft.locale),
      preferred_contact_method: draft.preferred_contact_method,
    };
  };

  const mut = useMutation({
    mutationFn: () => patchMeProfile(toBody()),
    onSuccess: () => {
      setSuccess(true);
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
      toast.success("Profile updated");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "That email is already in use.",
        );
      } else {
        setError(formatErr(err));
      }
      toast.error("Couldn't update profile", { description: formatErr(err) });
    },
  });

  const defaultTeamMutation = useMutation({
    mutationFn: (teamId: string) => saveScope({ team_id: teamId }),
    onSuccess: (payload) => {
      applyAuth(payload);
      applyScope(payload);
      queryClient.setQueryData(unscopedKey("me"), payload);
      removeAllScopedFromCache();
      revokeAllPlayerPhotos();
      toast.success("Default team updated");
    },
    onError: (err) => {
      toast.error("Couldn't update default team", { description: formatErr(err) });
    },
  });

  return (
    <form
      data-testid="me-profile-form"
      onSubmit={(e) => {
        e.preventDefault();
        setSuccess(false);
        setError(null);
        mut.mutate();
      }}
      className="flex flex-col gap-4"
    >
      <SectionHeader
        icon={<UserRound size={18} strokeWidth={2.2} />}
        title="Profile"
        description="Keep the contact details and regional preferences used across your tktic workspace."
      />

      <TeamAssignmentsPanel
        me={me}
        pendingTeamId={defaultTeamMutation.variables ?? null}
        saving={defaultTeamMutation.isPending}
        onSetDefault={(teamId) => defaultTeamMutation.mutate(teamId)}
      />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <TextField
          label="First name"
          value={draft.first_name}
          onChange={(v) => updateField("first_name", v)}
          testid="me-profile-first-name"
          maxLength={120}
        />
        <TextField
          label="Last name"
          value={draft.last_name}
          onChange={(v) => updateField("last_name", v)}
          testid="me-profile-last-name"
          maxLength={120}
        />
      </div>

      <TextField
        label="Email"
        type="email"
        value={draft.email}
        onChange={(v) => updateField("email", v)}
        testid="me-profile-email"
        autoComplete="email"
        maxLength={320}
      />
      <TextField
        label="Phone"
        type="tel"
        value={draft.phone}
        onChange={(v) => updateField("phone", v)}
        testid="me-profile-phone"
        autoComplete="tel"
        maxLength={64}
      />

      <label className="flex flex-col gap-1.5 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Timezone
        </span>
        {/* Make the browser-detected tz the first option when it's
         * outside the curated COMMON_TIMEZONES list. */}
        <TkticSelect
          value={draft.timezone}
          onChange={(next) => updateField("timezone", next)}
          data-testid="me-profile-timezone"
          ariaLabel="Timezone"
          options={[
            ...(!COMMON_TIMEZONES.includes(draft.timezone) && draft.timezone
              ? [
                  {
                    value: draft.timezone,
                    label: `${draft.timezone} (detected)`,
                  },
                ]
              : []),
            ...COMMON_TIMEZONES.map((tz) => ({ value: tz, label: tz })),
          ]}
        />
      </label>

      <label className="flex flex-col gap-1.5 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Language
        </span>
        <TkticSelect
          value={draft.locale}
          onChange={(next) => updateField("locale", next)}
          data-testid="me-profile-locale"
          ariaLabel="Language"
          options={COMMON_LOCALES.map((l) => ({
            value: l.value,
            label: l.label,
          }))}
        />
      </label>

      <div
        data-testid="me-profile-contact"
        className="flex flex-col gap-1.5 text-xs"
      >
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Preferred contact method
        </span>
        <div className="flex flex-wrap gap-1.5">
          {(["email", "phone", "none"] as const).map((method) => (
            <button
              key={method}
              type="button"
              onClick={() => updateField("preferred_contact_method", method)}
              data-testid={`me-profile-contact-${method}`}
              data-active={draft.preferred_contact_method === method ? "true" : undefined}
              className="inline-flex h-8 items-center rounded border border-rule px-3 font-display text-xs font-semibold uppercase tracking-wider text-ink-muted transition-colors hover:bg-field hover:text-ink data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
            >
              {method}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <p
          data-testid="me-profile-error"
          className="text-destructive text-xs font-mono"
        >
          {error}
        </p>
      ) : null}
      {success ? (
        <p
          data-testid="me-profile-success"
          className="text-good text-xs font-mono"
        >
          Profile saved.
        </p>
      ) : null}

      <div className="flex justify-end border-t border-rule pt-4">
        <Button
          type="submit"
          size="sm"
          disabled={mut.isPending}
          data-testid="me-profile-save"
        >
          <CheckCircle2 size={15} strokeWidth={2.2} />
          {mut.isPending ? "Saving…" : "Save profile"}
        </Button>
      </div>
    </form>
  );
}

function TeamAssignmentsPanel({
  me,
  pendingTeamId,
  saving,
  onSetDefault,
}: {
  me: MePayload;
  pendingTeamId: string | null;
  saving: boolean;
  onSetDefault: (teamId: string) => void;
}) {
  const defaultTeamId = profileDefaultTeamId(me);
  const hasMultipleTeams = me.teams.length > 1;
  return (
    <section
      data-testid="me-profile-teams"
      className="rounded border border-rule bg-elevated p-3 sm:p-4"
    >
      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <UsersRound size={16} strokeWidth={2.2} className="text-ink-muted" />
            <h3 className="font-display text-sm uppercase tracking-wider text-ink">
              Teams & default
            </h3>
          </div>
          <p className="mt-1 text-sm leading-snug text-ink-muted">
            Choose the team tktic should open first when you sign in.
          </p>
        </div>
        <span className="inline-flex h-7 items-center justify-center rounded-full border border-rule bg-surface px-2.5 font-mono text-[0.65rem] text-ink-muted">
          {me.teams.length} {me.teams.length === 1 ? "team" : "teams"}
        </span>
      </div>

      {me.teams.length > 0 ? (
        <div className="mt-3 grid gap-2">
          {me.teams.map((team) => {
            const isDefault = defaultTeamId === team.id;
            const pending = saving && pendingTeamId === team.id;
            const roleLabels = profileTeamRoleLabels(me, team);
            return (
              <div
                key={team.id}
                data-testid="me-profile-team-row"
                data-team-id={team.id}
                data-default={isDefault ? "true" : undefined}
                className="grid gap-3 rounded border border-rule bg-surface px-3 py-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
              >
                <div className="min-w-0">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <p className="min-w-0 truncate text-sm font-medium text-ink">
                      {team.name}
                    </p>
                    {isDefault ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-accent/60 bg-accent/10 px-2 py-0.5 font-display text-[0.58rem] uppercase tracking-wider text-accent">
                        <Star size={11} strokeWidth={2.4} aria-hidden="true" />
                        Default
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {roleLabels.map((label) => (
                      <span
                        key={label}
                        className="rounded border border-rule bg-elevated px-1.5 py-0.5 font-display text-[0.56rem] uppercase tracking-wider text-ink-muted"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
                {hasMultipleTeams ? (
                  <button
                    type="button"
                    onClick={() => onSetDefault(team.id)}
                    disabled={isDefault || saving}
                    data-testid={`me-profile-team-default-${team.id}`}
                    className="inline-flex h-8 items-center justify-center gap-2 rounded border border-rule px-3 font-display text-[0.65rem] font-semibold uppercase tracking-wider text-ink-muted transition-colors hover:border-accent hover:bg-field hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Star size={13} strokeWidth={2.2} aria-hidden="true" />
                    {pending ? "Saving..." : isDefault ? "Default" : "Set default"}
                  </button>
                ) : (
                  <span className="font-mono text-xs text-ink-muted">Only team</span>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="mt-3 rounded border border-rule bg-surface px-3 py-2 text-sm text-ink-muted">
          You are not assigned to any teams yet.
        </p>
      )}
    </section>
  );
}

export function profileDefaultTeamId(me: MePayload): string | null {
  return me.user.last_team_id || me.active_scope?.team.id || null;
}

export function profileTeamRoleLabels(me: MePayload, team: MeTeam): string[] {
  const roles = me.memberships
    .filter((membership) => membership.team_id === team.id)
    .map((membership) => roleLabel(membership.role));
  const uniqueRoles = Array.from(new Set(roles));
  return uniqueRoles.length > 0 ? uniqueRoles : [roleLabel(team.membership_role)];
}

function TextField({
  label,
  value,
  onChange,
  testid,
  type = "text",
  autoComplete,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  testid: string;
  type?: string;
  autoComplete?: string;
  maxLength?: number;
}) {
  return (
    <label className="flex flex-col gap-1.5 text-xs">
      <span className="font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        maxLength={maxLength}
        data-testid={testid}
        className="h-10 rounded border border-rule bg-field px-3 text-sm text-ink transition-colors placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent"
      />
    </label>
  );
}

// ---------------------------------------------------------------------------
// B4 — Password tab
// ---------------------------------------------------------------------------

function PasswordTab() {
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      changeMePassword({ current_password: current, new_password: next }),
    onSuccess: () => {
      setSuccess(true);
      setCurrent("");
      setNext("");
      setConfirm("");
      toast.success("Password updated");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 400) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "Current password is incorrect.",
        );
      } else if (err instanceof ApiError && err.status === 403) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "Password changes are only available for database users.",
        );
      } else {
        setError(formatErr(err));
      }
      toast.error("Couldn't change password", { description: formatErr(err) });
    },
  });

  return (
    <form
      data-testid="me-password-form"
      onSubmit={(e) => {
        e.preventDefault();
        setSuccess(false);
        setError(null);
        if (next.length < 8) {
          setError("New password must be at least 8 characters.");
          return;
        }
        if (next !== confirm) {
          setError("New password and confirmation don't match.");
          return;
        }
        mut.mutate();
      }}
      className="flex flex-col gap-4"
    >
      <SectionHeader
        icon={<LockKeyhole size={18} strokeWidth={2.2} />}
        title="Password"
        description="Update your password and revoke other open sessions from the same action."
      />
      <TextField
        label="Current password"
        type="password"
        value={current}
        onChange={setCurrent}
        testid="me-password-current"
        autoComplete="current-password"
      />
      <TextField
        label="New password"
        type="password"
        value={next}
        onChange={setNext}
        testid="me-password-new"
        autoComplete="new-password"
      />
      <TextField
        label="Confirm new password"
        type="password"
        value={confirm}
        onChange={setConfirm}
        testid="me-password-confirm"
        autoComplete="new-password"
      />

      <p className="rounded border border-rule bg-elevated px-3 py-2 text-xs text-ink-muted">
        Changing your password signs out every other session you have open.
      </p>

      {error ? (
        <p
          data-testid="me-password-error"
          className="text-destructive text-xs font-mono"
        >
          {error}
        </p>
      ) : null}
      {success ? (
        <p
          data-testid="me-password-success"
          className="text-good text-xs font-mono"
        >
          Password updated. Other sessions have been signed out.
        </p>
      ) : null}

      <div className="flex justify-end border-t border-rule pt-4">
        <Button
          type="submit"
          size="sm"
          disabled={mut.isPending}
          data-testid="me-password-save"
        >
          <KeyRound size={15} strokeWidth={2.2} />
          {mut.isPending ? "Saving…" : "Update password"}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// B5 — Email tab
// ---------------------------------------------------------------------------

function EmailTab({ profile }: { profile: UserProfile }) {
  const toast = useToast();
  const verified = !!profile.email_verified_at;
  const hasEmail = !!profile.email;
  const [devToken, setDevToken] = useState<string | null>(null);
  const [token, setToken] = useState("");
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [confirmSuccess, setConfirmSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const requestMut = useMutation({
    mutationFn: requestMeEmailVerification,
    onSuccess: (resp) => {
      setRequestSuccess(true);
      // Privacy invariant: dev-token surfaces ONLY when the backend
      // returns it (TKTIC_DEV_TOKEN_DELIVERY=1). Never logged.
      if (resp.verification_token) {
        setDevToken(resp.verification_token);
      }
      toast.success("Verification email sent");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 400) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "Add an email on the Profile tab before requesting verification.",
        );
      } else {
        setError(formatErr(err));
      }
      toast.error("Couldn't send verification email", {
        description: formatErr(err),
      });
    },
  });

  const confirmMut = useMutation({
    mutationFn: (t: string) => confirmEmailVerification({ token: t }),
    onSuccess: () => {
      setConfirmSuccess(true);
      setDevToken(null);
      setToken("");
      void queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
      toast.success("Email verified");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 400) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "That verification token is invalid or expired.",
        );
      } else {
        setError(formatErr(err));
      }
      toast.error("Couldn't verify email", { description: formatErr(err) });
    },
  });

  return (
    <section
      data-testid="me-email-section"
      className="flex flex-col gap-4"
    >
      <SectionHeader
        icon={<Mail size={18} strokeWidth={2.2} />}
        title="Email"
        description="Verify your email address so account recovery and team invites can be trusted."
      />

      <div className="flex flex-wrap items-center gap-2 rounded border border-rule bg-elevated px-3 py-2 text-sm">
        <span className="min-w-0 truncate text-ink">
          {profile.email || "(none on file — set one on the Profile tab)"}
        </span>
        {hasEmail ? (
          <span
            data-testid="me-email-verified-pill"
            data-verified={verified ? "true" : "false"}
            className={`rounded-full border px-2 py-0.5 font-display text-[0.55rem] font-semibold uppercase tracking-wider ${
              verified
                ? "border-good/40 bg-good/10 text-good"
                : "border-destructive/40 bg-elevated text-destructive"
            }`}
          >
            {verified ? "Verified" : "Unverified"}
          </span>
        ) : null}
      </div>
      {verified ? (
        <p className="text-[0.6rem] font-mono text-ink-muted">
          Verified on {profile.email_verified_at}
        </p>
      ) : hasEmail ? (
        <p className="text-[0.6rem] font-mono text-ink-muted">
          Email is on file but not verified. Send a verification email to
          confirm ownership.
        </p>
      ) : null}

      <div className="flex flex-col gap-3 rounded border border-rule bg-elevated p-3">
        <h3 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          Request verification
        </h3>
        <Button
          type="button"
          size="sm"
          disabled={!hasEmail || verified || requestMut.isPending}
          onClick={() => {
            setRequestSuccess(false);
            setError(null);
            requestMut.mutate();
          }}
          data-testid="me-email-request"
          className="self-end"
        >
          <Mail size={15} strokeWidth={2.2} />
          {requestMut.isPending ? "Sending…" : "Send verification email"}
        </Button>
        {requestSuccess ? (
          <p
            data-testid="me-email-request-success"
            className="text-[0.65rem] font-mono text-good"
          >
            Verification email sent. Paste the token below to confirm.
          </p>
        ) : null}
        {devToken ? (
          <div
            data-testid="me-email-dev-token"
            className="flex flex-col gap-1 rounded border border-rule bg-surface p-2 text-[0.6rem] font-mono"
          >
            <span className="text-ink-muted">Dev verification token:</span>
            <code className="text-ink break-all">{devToken}</code>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard?.writeText(devToken);
                setToken(devToken);
              }}
              data-testid="me-email-dev-copy"
              className="self-end rounded border border-rule px-2 py-0.5 font-display text-[0.6rem] uppercase tracking-wider text-ink-muted transition-colors hover:bg-field hover:text-ink"
            >
              Copy + paste into the confirm field
            </button>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 rounded border border-rule bg-elevated p-3">
        <h3 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          Confirm
        </h3>
        <label className="flex flex-col gap-1.5 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Verification token
          </span>
          <input
            type="text"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid="me-email-token"
            className="h-10 rounded border border-rule bg-field px-3 font-mono text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
        <Button
          type="button"
          size="sm"
          disabled={token.length < 16 || confirmMut.isPending}
          onClick={() => {
            setConfirmSuccess(false);
            setError(null);
            confirmMut.mutate(token.trim());
          }}
          data-testid="me-email-confirm"
          className="self-end"
        >
          <CheckCircle2 size={15} strokeWidth={2.2} />
          {confirmMut.isPending ? "Confirming…" : "Confirm email"}
        </Button>
        {confirmSuccess ? (
          <p
            data-testid="me-email-confirm-success"
            className="text-[0.65rem] font-mono text-good"
          >
            Email verified. Thank you for confirming.
          </p>
        ) : null}
      </div>

      {error ? (
        <p
          data-testid="me-email-error"
          className="text-destructive text-xs font-mono"
        >
          {error}
        </p>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// B6 — Sessions tab (minimal per locked scope; full list 🔵)
// ---------------------------------------------------------------------------

function SessionsTab() {
  const navigate = useNavigate({ from: "/me" });
  const [signOutPending, setSignOutPending] = useState(false);

  const onSignOut = async () => {
    if (signOutPending) return;
    setSignOutPending(true);
    try {
      try {
        await apiLogout();
      } catch {
        /* server may already have killed the session; clear locally */
      }
    } finally {
      applyExplicitLogoutBoundary();
      void navigate({ to: "/login", replace: true });
    }
  };

  return (
    <section
      data-testid="me-sessions-section"
      className="flex flex-col gap-4"
    >
      <SectionHeader
        icon={<Laptop size={18} strokeWidth={2.2} />}
        title="Sessions"
        description="Control the current browser session and understand how other sessions are revoked."
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded border border-rule bg-elevated p-3">
          <p className="font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
            Other sessions
          </p>
          <p className="mt-2 text-sm leading-snug text-ink">
            Change your password to sign out every other browser. The server
            revokes other sessions on that change.
          </p>
        </div>
        <div className="rounded border border-rule bg-elevated p-3">
          <p className="font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
            Device list
          </p>
          <p className="mt-2 text-sm leading-snug text-ink-muted">
            Last-used device and IP history is planned; the backend does not
            expose it yet.
          </p>
        </div>
      </div>
      <div className="flex justify-end border-t border-rule pt-4">
        <button
          type="button"
          onClick={() => void onSignOut()}
          disabled={signOutPending}
          data-testid="me-sessions-signout"
          className="inline-flex h-8 items-center justify-center gap-2 rounded border border-destructive/40 px-3 font-display text-sm font-semibold uppercase tracking-wider text-destructive transition-colors hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <LockKeyhole size={15} strokeWidth={2.2} />
          {signOutPending ? "Signing out…" : "Sign out of this session"}
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string"
      ? err.detail
      : `HTTP ${err.status}`;
  }
  return "Network error. Try again.";
}

function getInitials(label: string): string {
  const cleaned = label.trim();
  if (!cleaned) return "AC";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
  }
  return cleaned.slice(0, 2).toUpperCase();
}

function roleLabel(role: string): string {
  return role
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
